import styled from 'styled-components';
import { useCallback, MouseEvent } from 'react';
import {
  CUSTOM_DESCRIPTION_PREFIX,
  isCustomDescription,
  parseCustomDescription,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/rfqs-functions';
import {
  blackTangaroa,
  linkWater,
  paradigmLightBlue,
} from '@paradigm/design-system/src/colors';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import GridCellBase, {
  GridCellProps,
} from '#/components/UnifiedRfqs/components/GridCellBase';
import {
  CustomStrategyDescriptionContainer,
  DefinedStrategyDescription,
} from '#/components/UnifiedRfqs/components/strategy-description/StrategyDescription';
import LegsDescription from '#/components/UnifiedRfqs/components/strategy-description/LegsDescription';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { ROW_HEIGHT } from '#/components/rfq-common/TableRow';

interface Props {
  readonly children: string;
  readonly isExpanded: boolean;
  readonly className?: string;
  readonly onViewAll?: () => void;
}

const MAX_LENGTH_COLLAPSED = 2;
const MAX_LENGTH_EXPANDED = 6;

const getNumLegs = (isExpanded: boolean) =>
  isExpanded ? MAX_LENGTH_EXPANDED : MAX_LENGTH_COLLAPSED;

export default function ExpandableStrategyDescription({
  children,
  ...props
}: Props) {
  return (
    <StrategyDescriptionContainer role="cell" data-testid="description">
      <StrategyDescription {...props}>{children}</StrategyDescription>
    </StrategyDescriptionContainer>
  );
}

function StrategyDescription({ children, isExpanded, onViewAll }: Props) {
  const isMobile = useIsMobile();
  const isExpandedDesktop = isExpanded && !isMobile;
  if (isCustomDescription(children))
    return (
      <CustomStrategyDescription
        isExpanded={isExpandedDesktop}
        onViewAll={onViewAll}
      >
        {children}
      </CustomStrategyDescription>
    );

  return (
    <DefinedStrategyDescription maxNumLegs={getNumLegs(isExpandedDesktop)}>
      {children}
    </DefinedStrategyDescription>
  );
}

function CustomStrategyDescription({
  children,
  isExpanded,
  className,
  onViewAll,
}: Props) {
  const legs = parseCustomDescription(children);
  const maxNumLegs = getNumLegs(isExpanded);
  const shouldDisplayViewAll =
    onViewAll != null && isExpanded && legs.length > maxNumLegs;

  const viewAll = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onViewAll?.();
    },
    [onViewAll],
  );

  return (
    <CustomStrategyDescriptionContainer className={className}>
      <div>{CUSTOM_DESCRIPTION_PREFIX}</div>
      <div>
        <LegsDescription legs={legs} maxLength={maxNumLegs} />
        {shouldDisplayViewAll && <ViewAllLegsButton onClick={viewAll} />}
      </div>
    </CustomStrategyDescriptionContainer>
  );
}

const StrategyDescriptionContainer = styled(GridCellBase).attrs<GridCellProps>(
  () => ({
    area: 'desc',
    mobile: true,
  }),
)<GridCellProps>`
  color: ${linkWater};
  font-weight: 500;
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  overflow: visible;
  box-sizing: border-box;
  height: ${ROW_HEIGHT}px;};
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    height: 100%;
    font-weight: 600;
    color: white;
    justify-content: end;
    align-items: start;
    padding-top: 1rem;
    padding-right: 1rem;
  }
`;

const ViewAllLegsButton = styled.button.attrs({
  children: 'View All Legs',
})`
  margin-top: 8px;
  width: 84px;
  height: 24px;
  border: none;
  border-radius: 3px;
  box-sizing: border-box;
  font-size: 11px;
  line-height: 1;
  color: ${paradigmLightBlue};
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${blackTangaroa};
`;
